import { sumAllDistributions } from 'app/scripts/PricingHelper';
import DataMap from 'assets/components/dataMap/DataMap';
import DataObjectMap from 'assets/components/dataObjectMap/DataObjectMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { useSalesContractProvider } from '../../../Sales.Contract.Hooks';
import { ContractAdjustForm } from '../Contract.AdjustDistributions';

const ContractAdjustDistributionsEnhancementsDistributions = () => {
  const { lang } = getLocales();
  const { form } = useModalForm<ContractAdjustForm>();
  const { isDealer } = useAuthStore();
  const { selected } = useSalesContractProvider();

  const updateEnhancementByProductId = (
    productEnhancementId: number,
    updatedFields: Partial<Model.IContractEnhancement>
  ) => {
    const currentEnhancements = form.data?.contractEnhancements || [];

    const updatedEnhancements = currentEnhancements.map((enhancement) => {
      if (enhancement.productEnhancementId === productEnhancementId) {
        const merged = { ...enhancement, ...updatedFields };
        const { contractEnhancements } = selected ?? {};
        const oldContractEnhancement = contractEnhancements?.find(
          (it) => it.productEnhancementId === productEnhancementId
        );

        const pricingDistributionsSum = sumAllDistributions(merged.pricingDistributions);
        const distributorDistributionsSum = sumAllDistributions(merged.distributorDistributions);

        const discountPriceCAD =
          pricingDistributionsSum -
          oldContractEnhancement.originalPriceCAD +
          (distributorDistributionsSum - oldContractEnhancement.distributorPriceCAD);

        return {
          ...merged,
          discountPriceCAD,
          priceCAD:
            oldContractEnhancement.originalPriceCAD + oldContractEnhancement.distributorPriceCAD + discountPriceCAD,
        };
      }
      return enhancement;
    });

    form.update({ ...form.data, contractEnhancements: updatedEnhancements });
  };

  return (
    <FormWrapper>
      <p
        style={{
          backgroundColor: Theme.Color.warning,
          color: 'white',
          padding: `${Theme.Size.M} ${Theme.Size.L}`,
          fontWeight: 'bold',
          marginTop: 0,
        }}
      >
        {lang.contractUnrestrictedUpdateWarningMessage}
      </p>
      <DataMap
        data={form.data.contractEnhancements}
        render={({ data: contractEnhancement }) => {
          return (
            <>
              <h2>{contractEnhancement?.productEnhancement?.name || 'N/A'}</h2>
              &nbsp;
              <InputWrapper>
                <NumberInput
                  name="originalPriceCAD"
                  value={contractEnhancement?.originalPriceCAD}
                  label={lang.basePrice}
                  onChange={({ originalPriceCAD }) =>
                    updateEnhancementByProductId(contractEnhancement.productEnhancementId, {
                      originalPriceCAD,
                    })
                  }
                  icon={<Icon class={'fas-dollar-sign'} />}
                  min={0}
                  step={0.01}
                  roundOnBlur
                  readOnly
                />
                <NumberInput
                  name="distributorPriceCAD"
                  value={contractEnhancement?.distributorPriceCAD}
                  label={lang.distributorPrice}
                  onChange={({ distributorPriceCAD }) =>
                    updateEnhancementByProductId(contractEnhancement.productEnhancementId, {
                      distributorPriceCAD,
                    })
                  }
                  icon={<Icon class={'fas-dollar-sign'} />}
                  min={0}
                  step={0.01}
                  roundOnBlur
                  readOnly
                />
                <NumberInput
                  name="discountPriceCAD"
                  value={contractEnhancement?.discountPriceCAD}
                  label={lang.adjustedPrice}
                  onChange={({ discountPriceCAD }) =>
                    updateEnhancementByProductId(contractEnhancement.productEnhancementId, {
                      discountPriceCAD,
                    })
                  }
                  icon={<Icon class={'fas-dollar-sign'} />}
                  min={0}
                  step={0.01}
                  roundOnBlur
                  readOnly
                />
                <NumberInput
                  name="priceCAD"
                  value={contractEnhancement?.priceCAD}
                  label={lang.price}
                  onChange={({ priceCAD }) =>
                    updateEnhancementByProductId(contractEnhancement.productEnhancementId, {
                      priceCAD,
                    })
                  }
                  icon={<Icon class={'fas-dollar-sign'} />}
                  min={0}
                  step={0.01}
                  roundOnBlur
                  readOnly
                />
              </InputWrapper>
              <table className={ThemeTableSimple}>
                <thead>
                  <tr>
                    <th>{lang.pricingDistributions}</th>
                    <th>{lang.originalPrice}</th>
                    <th>{lang.adjustedPrice}</th>
                  </tr>
                </thead>
                <tbody>
                  <DataObjectMap
                    data={contractEnhancement.pricingDistributions}
                    render={({ data: pricingInfo, key }) => {
                      const old = contractEnhancement.pricingDistributions[key];
                      return (
                        <tr key={key}>
                          <td>{key}</td>
                          <td>
                            <FormatValue value={`${(old?.amount ?? 0).toFixed(2)}$`} />
                          </td>
                          <td>
                            <NumberInput
                              name="amount"
                              value={pricingInfo.amount}
                              onChange={({ amount }) =>
                                updateEnhancementByProductId(contractEnhancement.productEnhancementId, {
                                  pricingDistributions: {
                                    ...contractEnhancement.pricingDistributions,
                                    [key]: {
                                      ...contractEnhancement.pricingDistributions[key],
                                      amount,
                                    },
                                  },
                                })
                              }
                              style={{
                                formInput: {
                                  minWidth: '157px',
                                },
                                input: {
                                  textAlign: 'right',
                                },
                              }}
                              icon={<Icon class={'fas-dollar-sign'} />}
                              min={0}
                              step={0.01}
                              roundOnBlur
                            />
                          </td>
                        </tr>
                      );
                    }}
                  />
                </tbody>
                {!isDealer && (
                  <>
                    <thead>
                      <tr>
                        <th>{lang.distributorDistributions}</th>
                        <th>{lang.originalPrice}</th>
                        <th>{lang.adjustedPrice}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <DataObjectMap
                        data={contractEnhancement.distributorDistributions}
                        render={({ data: pricingInfo, key }) => {
                          const old = contractEnhancement.distributorDistributions[key];
                          return (
                            <tr key={key}>
                              <td>{key}</td>
                              <td>
                                <FormatValue value={`${(old?.amount ?? 0).toFixed(2)}$`} />
                              </td>
                              <td>
                                <NumberInput
                                  name="amount"
                                  value={pricingInfo.amount}
                                  onChange={({ amount }) =>
                                    updateEnhancementByProductId(contractEnhancement.productEnhancementId, {
                                      distributorDistributions: {
                                        ...contractEnhancement.distributorDistributions,
                                        [key]: {
                                          ...contractEnhancement.distributorDistributions[key],
                                          amount,
                                        },
                                      },
                                    })
                                  }
                                  style={{
                                    formInput: {
                                      minWidth: '157px',
                                    },
                                    input: {
                                      textAlign: 'right',
                                    },
                                  }}
                                  icon={<Icon class={'fas-dollar-sign'} />}
                                  min={0}
                                  step={0.01}
                                  roundOnBlur
                                />
                              </td>
                            </tr>
                          );
                        }}
                      />
                    </tbody>
                  </>
                )}
              </table>
            </>
          );
        }}
      />
    </FormWrapper>
  );
};

export default ContractAdjustDistributionsEnhancementsDistributions;
